import Vue from 'vue'
import ProcessFlowBuilderModel from '~/models/tracked/processFlowBuilderModel'
import IConditionalRuleContainer from '~/models/conditionalRules/conditionalRuleContainer'
import {IIterationStep, IProcessFlow, IProcessFlowStep, IProcessFlowStepAction, IProcessFlowStepContainer, IProcessFlowVariable} from '~/interfaces/processFlowInterfaces'
import ProcessFlowStepActionJumpTo from '~/models/processFlows/processFlowStepActionJumpTo'
import Guid from '~/helpers/guid';
import ProcessFlowStepElementConditionalRuleSet from '~/models/processFlows/processFlowStepElementConditionalRuleSet'
import { Hash } from '~/interfaces/hashInterfaces';
import Repo from '~/state/modules/repo'
import IRepo from '~/interfaces/repo';
import ProcessFlowStepElementConditionalFields from '~/models/processFlows/processFlowStepElementConditionalFields'
import ProcessFlowStep from './processFlowStep';
import ProcessFlowStepActionJumpToFactory from './processFlowStepActionJumpToFactory';
import {RelationType} from '~/models/relationType'
import {ConditionalFieldDefinition} from '~/models/conditionalRules/conditionalFieldDefinition'
import {ConditionalRuleEmbeddedModelsHelper} from '~/models/conditionalRules/helpers/conditionalRuleEmbeddedModelsHelper'
import ProcessFlowMappedVariable from '~/models/processFlows/steps/processFlowMappedVariable'
import IWorkflowStepContainer from '../../../formability/form_builder/models/workflow/iworkflowStepContainer'
import {IHasParent} from '~/interfaces/modelInterfaces'

export default class ProcessFlowStepAction extends ProcessFlowBuilderModel implements IConditionalRuleContainer, IWorkflowStepContainer, IProcessFlowStepAction {
  client_id: string = Guid.create();
  title: string;
  sort_order: number;
  style: string = '';
  createEmbeddedObject = {};
  validate_fields: boolean;
  jump_to: ProcessFlowStepActionJumpTo;
  jump_to_conditional_rule_sets: Hash<ProcessFlowStepElementConditionalRuleSet>;
  jump_to_mapped_variable_references: Hash<ProcessFlowMappedVariable> = {};
  readonly JumpToFieldName = 'jump_to';

  static repo: IRepo<ProcessFlowStepAction> = new Repo<ProcessFlowStepAction>('processFlowStepActions', ProcessFlowStepAction);

  constructor(processFlowStep: ProcessFlowStep, json: any = {}) {
    super();
    this.defineProperty<this>('client_id', json['client_id'] || Guid.create());
    this.defineProperty<this>('title', '');
    this.defineProperty<this>('validate_fields', ('validate_fields' in json) ? json['validate_fields'] : true);
    const initialSortOrder = json['sort_order'] === 0 || json['sort_order'] ? json['sort_order'] : Object.keys(processFlowStep.actions).length;
    this.defineProperty<this>('sort_order', initialSortOrder);
    this.defineProperty<this>('style', 'green');

    ConditionalRuleEmbeddedModelsHelper.defineEmbeddedModelsCreation(this, ProcessFlowStepElementConditionalFields.PROCESS_FLOW_STEP_ACTION_FIELDS)

    this.createEmbeddedObject['jump_to_mapped_variable_references'] = (parent, refJson) => {
      return new ProcessFlowMappedVariable('jump_to_mapped_variable_references').loadFromJson(refJson);
    }

    this.loadFromJson(json, processFlowStep.rootModel);
    if (!this.jump_to) {
      ProcessFlowStepActionJumpToFactory.create(this)
    }

    this.rootModel = processFlowStep.rootModel;
    this.parent = processFlowStep;
    ProcessFlowStepAction.repo.add(this);
    Vue.set(processFlowStep.actions, this.client_id, this);
  }

  get EMBEDDED_MODELS(): { [key: string]: { type: any, relationType: RelationType } } {
    const models = {
      jump_to_mapped_variable_references: { type: ProcessFlowMappedVariable, relationType: RelationType.dictionary },
    }

    ConditionalRuleEmbeddedModelsHelper.addEmbeddedModels(
      models,
      ProcessFlowStepElementConditionalFields.PROCESS_FLOW_STEP_ACTION_FIELDS,
      ProcessFlowStepElementConditionalRuleSet,
      ProcessFlowStepActionJumpTo,
    )
    return models;
  }

  get EMBEDDED_HASHES(): string[] {
    return [this.JumpToFieldName];
  }

  get titleOrDefault(): string {
    return this.title;
  }

  get rootName(): string {
    return 'actions';
  }

  get isCollectionRoot(): boolean {
    return true;
  }

  getConditionalRuleSets(fieldName: string): { [key: string]: ProcessFlowStepElementConditionalRuleSet } {
    return this[`${fieldName}_conditional_rule_sets`];
  }

  getSortedConditionalRuleSets(fieldName: string): ProcessFlowStepElementConditionalRuleSet[] {
    const ruleSets = this.getConditionalRuleSets(fieldName);
    return Object.keys(this.getConditionalRuleSets(fieldName)).map(key => ruleSets[key])
      .sort((x, y) => x.sort_order < y.sort_order ? -1 : 1);
  }

  getJumpToConditionals() {
    return this.getConditionalRuleSets(this.JumpToFieldName);
  }

  getSortedJumpToConditionals(): ProcessFlowStepElementConditionalRuleSet[] {
    return this.getSortedConditionalRuleSets(this.JumpToFieldName);
  }

  get nextSteps(): any[] {
    return this.jumpTos.map(k => this.stepParent.steps[k.to_client_id] ? this.stepParent.steps[k.to_client_id] : k);
  }

  get jumpTos(): ProcessFlowStepActionJumpTo[] {
    const values = [];
    const ruleSets = this.getSortedJumpToConditionals();

    Object.keys(ruleSets).forEach(k => values.push(ruleSets[k].conditional_value));
    values.push(this.jump_to);
    return values
  }

  createNewRuleSet(fieldName: string, definition: ConditionalFieldDefinition): ProcessFlowStepElementConditionalRuleSet {
    return new ProcessFlowStepElementConditionalRuleSet(fieldName, definition);
  }

  getReferencedControl(id: string) {
    return this.jump_to_mapped_variable_references[id]
  }

  getReferencableControls() {
    throw new Error('not implemented')
    return null
  }

  createMappedVariableReference(fieldName: string): ProcessFlowMappedVariable {
    const reference = new ProcessFlowMappedVariable(`${fieldName}_mapped_variable_references`);
    reference.attachToParent(this);
    return reference;
  }

  /**
   * For common interface with ProcessFlowStepAction for linking steps and attaching to appropriate parent
   */
  initializeStep(item: IProcessFlowStep & IHasParent<IProcessFlow | IIterationStep>, position: number = null, json: object = {}, selectItem: boolean = true) {
    this.parent.initializeStep(item, position, json, selectItem)
  }

  /**
   * As of now, actions are always contained in a DisplayAForm step where it's parent will always be a IProcessFlowStepContainer
   */
  get stepParent(): IProcessFlowStepContainer {
    return this.parent.parent
  }
}
